import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import QuillEditor from "../../../../components/shared/QuillEditor"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"

const CTAEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()

  const [formData, setFormData] = useState({
    headline: section?.headline || "",
    subheadline: section?.subheadline || "",
    body: section?.body || "",
    imageUrl: section?.imageUrl || "",
    settings: section?.settings || {
      layout: "content_centered", // content_centered, image_right, image_left, half_image_right, half_image_left, highlights_2, highlights_3
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      highlightTitleFontSize: "18px", // Font size for highlight titles
      highlightDescriptionFontSize: "14px", // Font size for highlight descriptions
      highlights: [
        { title: "", description: { html: "", delta: null }, imageUrl: "" },
        { title: "", description: { html: "", delta: null }, imageUrl: "" },
        { title: "", description: { html: "", delta: null }, imageUrl: "" }
      ]
    }
  })
  const [activeHighlight, setActiveHighlight] = useState(0)
  const [errors, setErrors] = useState({})
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // GraphQL mutation for uploading images
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  // Calculate layout-dependent variables early
  const isHighlightLayout = formData.settings.layout === "highlights_2" || formData.settings.layout === "highlights_3"
  const maxHighlights = formData.settings.layout === "highlights_2" ? 2 : 3

  // Helper to get highlights from settings
  const getHighlights = () => formData.settings.highlights || []

  useEffect(() => {
    if (section) {
      // Handle migration from old highlights column to new settings.highlights
      const legacyHighlights = section.highlights || []
      const settingsHighlights = section.settings?.highlights || []

      // Use settings.highlights if available, otherwise migrate from legacy highlights
      let initialHighlights = []
      if (settingsHighlights.length > 0) {
        initialHighlights = settingsHighlights
      } else if (legacyHighlights.length > 0) {
        // Migrate legacy format
        initialHighlights = legacyHighlights.map((highlight) => ({
          title: highlight.title || "",
          description: {
            html: highlight.description || "",
            delta: null // We don't have delta for legacy data
          },
          imageUrl: highlight.imageUrl || ""
        }))
      } else {
        // Default empty highlights
        initialHighlights = [
          { title: "", description: { html: "", delta: null }, imageUrl: "" },
          { title: "", description: { html: "", delta: null }, imageUrl: "" },
          { title: "", description: { html: "", delta: null }, imageUrl: "" }
        ]
      }

      setFormData({
        headline: section.headline || "",
        subheadline: section.subheadline || "",
        body: section.body || "",
        imageUrl: section.imageUrl || "",
        settings: {
          layout: "content_centered",
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          highlightTitleFontSize: "18px",
          highlightDescriptionFontSize: "14px",
          ...section.settings,
          highlights: initialHighlights,
          // Remove deprecated color settings
          titleColor: undefined,
          subheadlineColor: undefined,
          descriptionColor: undefined
        }
      })

      // Reset active highlight to 0 when section changes
      setActiveHighlight(0)
    }
  }, [section, practice])

  // Ensure active highlight is within bounds when layout changes
  useEffect(() => {
    const currentMaxHighlights = formData.settings.layout === "highlights_2" ? 2 : 3
    if (activeHighlight >= currentMaxHighlights) {
      setActiveHighlight(0)
    }
  }, [formData.settings.layout, activeHighlight])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Update preview immediately for content changes
    if (section?.id && ["headline", "subheadline", "body", "imageUrl"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // For background color, ensure showBackgroundColor matches backgroundColor
      // When backgroundColor is "theme", showBackgroundColor should be true
      // When backgroundColor is "#ffffff" or "#FFF", showBackgroundColor should be false
      if (key === "backgroundColor") {
        newSettings.showBackgroundColor = value === "theme"
      } else if (key === "showBackgroundColor") {
        // When showBackgroundColor changes, sync backgroundColor
        newSettings.backgroundColor = value ? "theme" : "#ffffff"
      }

      // Update preview immediately for layout changes, color changes, and other important settings
      if (
        section?.id &&
        [
          "layout",
          "showBackgroundColor",
          "backgroundColor",
          "highlightTitleFontSize",
          "highlightDescriptionFontSize"
        ].includes(key)
      ) {
        // Use functional update to ensure we have the latest settings
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  const handleHighlightChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedHighlights = [...(prev.settings.highlights || [])]

      if (field === "description" && typeof value === "object" && value.html !== undefined) {
        // Handle rich text with delta
        updatedHighlights[index] = {
          ...updatedHighlights[index],
          description: value
        }
      } else {
        // Handle other fields (title, imageUrl)
        updatedHighlights[index] = {
          ...updatedHighlights[index],
          [field]: value
        }
      }

      const newFormData = {
        ...prev,
        settings: {
          ...prev.settings,
          highlights: updatedHighlights
        }
      }

      // Update preview immediately for highlight changes
      if (section?.id) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...newFormData
        })
      }

      return newFormData
    })
  }

  // Handle image selection from ImagePickerV2
  const handleImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle: handle,
      description: "cta_section_image",
      provider: provider
    })
      .then((result) => {
        if (result?.data?.uploadImage?.result === "success") {
          handleChange("imageUrl", result.data.uploadImage.photoUrl)
          showToast("Image uploaded successfully!")
        } else {
          console.error(result) // eslint-disable-line no-console
          let errorMessage = "There was an error uploading your image. Please try again later or contact support."
          if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
          showToast({
            type: "error",
            content: errorMessage
          })
        }
      })
      .catch((error) => {
        console.error("Upload error:", error) // eslint-disable-line no-console
        showToast({
          type: "error",
          content: "There was an error uploading your image. Please try again."
        })
      })
  }

  // Handle image removal
  const handleImageRemove = () => {
    handleChange("imageUrl", "")
    showToast("Image removed.")
  }

  // Handle highlight image selection from ImagePickerV2
  const handleHighlightImageSelect = ({ handle, provider }, highlightIndex) => {
    uploadImage({
      handle: handle,
      description: "cta_highlight_image",
      provider: provider
    })
      .then((result) => {
        if (result?.data?.uploadImage?.result === "success") {
          handleHighlightChange(highlightIndex, "imageUrl", result.data.uploadImage.photoUrl)
          showToast("Image uploaded successfully!")
        } else {
          console.error(result) // eslint-disable-line no-console
          let errorMessage = "There was an error uploading your image. Please try again later or contact support."
          if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
          showToast({
            type: "error",
            content: errorMessage
          })
        }
      })
      .catch((error) => {
        console.error("Upload error:", error) // eslint-disable-line no-console
        showToast({
          type: "error",
          content: "There was an error uploading your image. Please try again."
        })
      })
  }

  // Handle highlight image removal
  const handleHighlightImageRemove = (highlightIndex) => {
    handleHighlightChange(highlightIndex, "imageUrl", "")
    showToast("Image removed.")
  }

  // Layout options with preview images - updated to match HeroSection.jsx patterns
  const layoutOptions = [
    {
      id: "content_centered",
      name: "Content Centered",
      description: "Centered content with background image",
      image: require("../../../../../assets/images/builder/v2/layout-images/content-centered.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/content-centered-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/content-centered-selected.png")
    },
    {
      id: "image_right",
      name: "Image Right",
      description: "Content on left, image on right",
      image: require("../../../../../assets/images/builder/v2/layout-images/img-r-no-cta.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-r-no-cta-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-r-no-cta-selected.png")
    },
    {
      id: "image_left",
      name: "Image Left",
      description: "Content on right, image on left",
      image: require("../../../../../assets/images/builder/v2/layout-images/img-l-no-cta.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-l-no-cta-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-l-no-cta-selected.png")
    },
    {
      id: "half_image_right",
      name: "Half Image Right",
      description: "Half-width image on the right",
      image: require("../../../../../assets/images/builder/v2/layout-images/overlap-img-r-no-cta.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/overlap-img-r-no-cta-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/overlap-img-r-no-cta-selected.png")
    },
    {
      id: "half_image_left",
      name: "Half Image Left",
      description: "Half-width image on the left",
      image: require("../../../../../assets/images/builder/v2/layout-images/overlap-img-l-no-cta.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/overlap-img-l-no-cta-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/overlap-img-l-no-cta-selected.png")
    },
    {
      id: "highlights_2",
      name: "2 Highlights",
      description: "Two highlighted content blocks",
      image: require("../../../../../assets/images/builder/v2/layout-images/2-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-selected.png")
    },
    {
      id: "highlights_3",
      name: "3 Highlights",
      description: "Three highlighted content blocks",
      image: require("../../../../../assets/images/builder/v2/layout-images/3-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        {/* Fallback placeholder */}
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  // Close button - discard changes and close immediately
  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  // Confirm save from modal
  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    if (validateForm()) {
      const currentHighlights = getHighlights()
      const trimmedHighlights = currentHighlights.slice(0, maxHighlights)

      onSave({
        layout: "cta_content",
        headline: formData.headline,
        subheadline: formData.subheadline,
        body: formData.body,
        imageUrl: formData.imageUrl,
        settings: {
          ...formData.settings,
          highlights: trimmedHighlights
        }
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Allow empty headline - user can remove content if they want
    // if (!formData.headline.trim()) {
    //   newErrors.headline = "Headline is required"
    // }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      setShowConfirmModal(true)
    }
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        {/* Section Title Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a short title to describe this section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Enter your section title..."
                className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                rows={3}
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Subheadline Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Subheadline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a supporting subtitle for this section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-16 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.subheadline}
                onChange={(e) => handleChange("subheadline", e.target.value)}
                placeholder="Enter your subheadline..."
                className="box-border flex h-16 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Description</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a detailed description to provide more context.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg">
              <QuillEditor
                value={formData.body}
                onChange={(value) => handleChange("body", value)}
                placeholder="Tell visitors about your call-to-action and what makes it compelling..."
                error={errors.body}
              />
            </div>
          </div>
        </div>

        {/* Image Section - only show for non-highlight layouts */}
        {!isHighlightLayout && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                <p className="block leading-[24px]">Image</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                <p className="block leading-[20px]">Select an image to feature in this section.</p>
              </div>
            </div>

            {/* Image Upload Area */}
            {formData.imageUrl ? (
              <div className="mb-6 flex items-center justify-center">
                <div className="flex items-center gap-4">
                  <img src={formData.imageUrl} alt="CTA section image" className="h-16 w-16 rounded-lg object-cover" />
                  <div className="flex flex-col gap-2">
                    <ImagePickerV2
                      practice={practice}
                      onImageSelect={handleImageSelect}
                      fetching={uploadFetching}
                      showCurateTab={true}
                      currentImageUrl={formData.imageUrl}
                      trigger={
                        <button className="font-bold text-[#0d9da4] hover:underline" disabled={uploadFetching}>
                          Edit image
                        </button>
                      }
                    />
                    <button
                      className="font-bold text-red-500 hover:underline"
                      onClick={handleImageRemove}
                      disabled={uploadFetching}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <ImagePickerV2
                practice={practice}
                onImageSelect={handleImageSelect}
                fetching={uploadFetching}
                showCurateTab={true}
                currentImageUrl={formData.imageUrl}
                trigger={
                  <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#c8d6df] bg-white p-6 transition-colors hover:border-[#0d9da4]">
                    <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-[#e2f3f4]">
                      <svg className="h-5 w-5 text-[#0d9da4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0d9da4]">
                      <p className="whitespace-pre leading-[24px]">
                        <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                      </p>
                    </div>
                    <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                      <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                    </div>
                  </div>
                }
              />
            )}
          </div>
        )}

        {/* Highlights Section - only show for highlight layouts */}
        {isHighlightLayout && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[20px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[24px]">Highlights</p>
            </div>

            {/* Tab Bar */}
            <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-8 p-0 [flex-flow:wrap]">
              <div className="pointer-events-none absolute inset-x-0 bottom-[-1px] top-0 border-solid border-[#e4eff6] border-[0px_0px_1px]" />
              {Array.from({ length: maxHighlights }, (_, index) => (
                <div
                  key={index}
                  className="relative box-border flex h-8 shrink-0 cursor-pointer flex-col content-stretch items-start justify-start gap-1.5 p-0"
                  onClick={() => setActiveHighlight(index)}>
                  <div
                    className={`relative shrink-0 text-nowrap text-left text-[16px] leading-[0] ${
                      activeHighlight === index
                        ? "font-['Manrope:Bold',_sans-serif] font-bold text-[#0b3954]"
                        : "font-['Manrope:Regular',_sans-serif] font-normal text-[#0d9da4]"
                    }`}>
                    <p className="block whitespace-pre leading-[24px]">Highlight {index + 1}</p>
                  </div>
                  {activeHighlight === index && <div className="h-0.5 w-full shrink-0 bg-[#0b3954]" />}
                </div>
              ))}
            </div>

            {/* Active Highlight Content */}
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
              {/* Highlight Title */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
                  <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                    <p className="block leading-[24px]">Title</p>
                  </div>
                  <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                    <p className="block leading-[20px]">Add a short title to describe this highlight.</p>
                  </div>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
                    <textarea
                      value={getHighlights()[activeHighlight]?.title || ""}
                      onChange={(e) => handleHighlightChange(activeHighlight, "title", e.target.value)}
                      placeholder="Enter highlight title..."
                      className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Highlight Title Font Size */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
                  <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                    <p className="block leading-[24px]">Title font size</p>
                  </div>
                  <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                    <p className="block leading-[20px]">Choose the font size for highlight titles.</p>
                  </div>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <select
                    value={formData.settings.highlightTitleFontSize || "18px"}
                    onChange={(e) => handleSettingsChange("highlightTitleFontSize", e.target.value)}
                    className="box-border flex h-10 w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                    <option value="14px">Small (14px)</option>
                    <option value="16px">Medium (16px)</option>
                    <option value="18px">Large (18px)</option>
                    <option value="20px">Extra Large (20px)</option>
                    <option value="22px">XXL (22px)</option>
                    <option value="24px">XXXL (24px)</option>
                  </select>
                </div>
              </div>

              {/* Highlight Description */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
                  <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                    <p className="block leading-[24px]">Description</p>
                  </div>
                  <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                    <p className="block leading-[20px]">Add a detailed description to provide more context.</p>
                  </div>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative w-full shrink-0 rounded-lg">
                    <QuillEditor
                      key={`highlight-${activeHighlight}-description`}
                      value={getHighlights()[activeHighlight]?.description?.html || ""}
                      onChangeWithDelta={(data) => handleHighlightChange(activeHighlight, "description", data)}
                      placeholder="Describe this highlight..."
                    />
                  </div>
                </div>
              </div>

              {/* Highlight Description Font Size */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
                  <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                    <p className="block leading-[24px]">Description font size</p>
                  </div>
                  <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                    <p className="block leading-[20px]">Choose the font size for highlight descriptions.</p>
                  </div>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <select
                    value={formData.settings.highlightDescriptionFontSize || "14px"}
                    onChange={(e) => handleSettingsChange("highlightDescriptionFontSize", e.target.value)}
                    className="box-border flex h-10 w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                    <option value="12px">Small (12px)</option>
                    <option value="14px">Medium (14px)</option>
                    <option value="16px">Large (16px)</option>
                    <option value="18px">Extra Large (18px)</option>
                    <option value="20px">XXL (20px)</option>
                  </select>
                </div>
              </div>

              {/* Highlight Image */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
                  <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
                    <p className="block leading-[24px]">Image</p>
                  </div>
                  <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
                    <p className="block leading-[20px]">Select an image to feature in this highlight.</p>
                  </div>
                </div>

                {/* Highlight Image Upload Area */}
                {getHighlights()[activeHighlight]?.imageUrl ? (
                  <div className="mb-6 flex items-center justify-center">
                    <div className="flex items-center gap-4">
                      <img
                        src={getHighlights()[activeHighlight].imageUrl}
                        alt="Highlight image"
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                      <div className="flex flex-col gap-2">
                        <ImagePickerV2
                          practice={practice}
                          onImageSelect={(data) => handleHighlightImageSelect(data, activeHighlight)}
                          fetching={uploadFetching}
                          showCurateTab={true}
                          currentImageUrl={getHighlights()[activeHighlight]?.imageUrl}
                          trigger={
                            <button className="font-bold text-[#0d9da4] hover:underline" disabled={uploadFetching}>
                              Edit image
                            </button>
                          }
                        />
                        <button
                          className="font-bold text-red-500 hover:underline"
                          onClick={() => handleHighlightImageRemove(activeHighlight)}
                          disabled={uploadFetching}>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ImagePickerV2
                    practice={practice}
                    onImageSelect={(data) => handleHighlightImageSelect(data, activeHighlight)}
                    fetching={uploadFetching}
                    showCurateTab={true}
                    currentImageUrl={getHighlights()[activeHighlight]?.imageUrl}
                    trigger={
                      <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#c8d6df] bg-white p-6 transition-colors hover:border-[#0d9da4]">
                        <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-[#e2f3f4]">
                          <svg className="h-5 w-5 text-[#0d9da4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                        </div>
                        <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0d9da4]">
                          <p className="whitespace-pre leading-[24px]">
                            <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                          </p>
                        </div>
                        <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                          <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Background Color Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <BackgroundColorToggle
            formData={formData}
            onSettingsChange={handleSettingsChange}
            sectionType="cta_content"
          />
        </div>
      </div>

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

export default CTAEditor
